import { useEffect, useState, useCallback } from "react";
import { Users, UserPlus, Trash2, CheckCircle2, Clock, Mail, Crown } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * Owner-only section for managing staff (empleados) invitations.
 * Hidden entirely from staff users. Requires owner.plan === 'pro' to invite.
 */
export default function StaffManager({ ownerPlan }) {
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/staff");
            setStaff(data);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.code !== "pro_required") {
                toast.error(typeof detail === "string" ? detail : "Error cargando empleados");
            }
            setStaff([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const invite = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setSubmitting(true);
        try {
            await api.post("/staff", { email: email.trim(), name: name.trim() || null });
            toast.success("Invitación enviada");
            setEmail("");
            setName("");
            load();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "object" ? detail.message : (detail || "No se pudo invitar"));
        } finally {
            setSubmitting(false);
        }
    };

    const removeStaff = async (row) => {
        if (!window.confirm(`Quitar a ${row.email} del equipo?`)) return;
        try {
            await api.delete(`/staff/${encodeURIComponent(row.email)}`);
            toast.success("Empleado eliminado");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "No se pudo eliminar");
        }
    };

    if (ownerPlan !== "pro") {
        return (
            <div className="card-clean p-6 md:p-8" data-testid="staff-manager">
                <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-amber-700" />
                    </div>
                    <div className="flex-1">
                        <h3 className="font-display text-xl font-semibold text-[#022C22]">Cuentas de empleados</h3>
                        <p className="text-sm text-[#64748B] mt-1">
                            Invitá a tus cajeros y empleados a ver tus pagos en tiempo real (sin acceso a ganancias ni configuración). Es exclusivo del Plan Pyme Pro.
                        </p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                        <Crown className="w-3 h-3" /> Pyme Pro
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="card-clean p-6 md:p-8" data-testid="staff-manager">
            <div className="flex items-start gap-3 mb-5">
                <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-emerald-700" />
                </div>
                <div className="flex-1">
                    <h3 className="font-display text-xl font-semibold text-[#022C22]">Cuentas de empleados</h3>
                    <p className="text-sm text-[#64748B] mt-1">
                        Tus cajeros y empleados ven los pagos en tiempo real con sonido, pero NO pueden ver ganancias totales, reportes ni configuración.
                    </p>
                </div>
            </div>

            <form onSubmit={invite} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 mb-5" data-testid="staff-invite-form">
                <input
                    type="email"
                    placeholder="correo@empleado.cr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="input-clean"
                    data-testid="staff-email-input"
                />
                <input
                    type="text"
                    placeholder="Nombre (opcional)"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-clean"
                    data-testid="staff-name-input"
                />
                <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="staff-invite-button"
                >
                    {submitting ? (
                        <span className="h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    ) : (
                        <UserPlus className="w-4 h-4" />
                    )}
                    Invitar
                </button>
            </form>

            {loading ? (
                <div className="py-10 text-center text-[#64748B]" data-testid="staff-loading">Cargando…</div>
            ) : staff.length === 0 ? (
                <div className="py-10 text-center text-sm text-[#94A3B8]" data-testid="staff-empty">
                    Aún no invitaste a nadie. Ingresá un correo arriba.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="staff-table">
                        <thead>
                            <tr className="text-left border-b border-[#E2E8F0] bg-[#F8FAFC]">
                                <th className="px-4 py-3 font-medium text-[#64748B] uppercase tracking-wider text-xs">Empleado</th>
                                <th className="px-4 py-3 font-medium text-[#64748B] uppercase tracking-wider text-xs">Estado</th>
                                <th className="px-4 py-3 font-medium text-[#64748B] uppercase tracking-wider text-xs">Invitado</th>
                                <th className="px-4 py-3 font-medium text-[#64748B] uppercase tracking-wider text-xs text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {staff.map((s) => (
                                <tr key={s.email} className="border-b border-[#E2E8F0]" data-testid={`staff-row-${s.email}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            {s.picture ? (
                                                <img src={s.picture} alt={s.name || s.email} className="w-8 h-8 rounded-full object-cover" />
                                            ) : (
                                                <div className="w-8 h-8 rounded-full bg-[#064E3B] flex items-center justify-center text-white text-xs font-medium">
                                                    {(s.name || s.email)[0].toUpperCase()}
                                                </div>
                                            )}
                                            <div>
                                                <p className="font-medium text-[#022C22]">{s.name || s.email.split("@")[0]}</p>
                                                <p className="text-xs text-[#64748B] flex items-center gap-1">
                                                    <Mail className="w-3 h-3" /> {s.email}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        {s.consumed ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium">
                                                <CheckCircle2 className="w-3 h-3" /> Activo
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
                                                <Clock className="w-3 h-3" /> Pendiente
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-[#64748B] text-xs">
                                        {s.invited_at ? new Date(s.invited_at).toLocaleDateString("es-CR") : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <button
                                            onClick={() => removeStaff(s)}
                                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-red-700 hover:bg-red-50 text-xs"
                                            data-testid={`staff-remove-${s.email}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" /> Quitar
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
